import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    Button,
    TablePagination,
    TextField,
    InputAdornment,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { getAllAdmins, getReportViewersByCoeId } from "../../../services/adminServices/CoeServices";
import SearchIcon from "@mui/icons-material/Search";
import AddAdminToCoe from "./AddAdminToCoe";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AddNewViewerModal from "./AddNewViewerModal";
import { toast } from "react-toastify";
import { unmapAdmin } from "../../../services/adminServices/AdminServices";

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 13,
        textTransform: "capitalize",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    minHeight: "50%",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "8px",
    outline: "none !important",
};

const AdminListModal = ({ open, onClose, selectedRow, fetchCoeData, user, adminListType }) => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [search, setSearch] = useState("");
    const [addAdminOpen, setAddAdminOpen] = useState(false);
    const [addNewViewerOpen, setAddNewViewerOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            let response;
            if (adminListType === 'viewer') {
                response = await getReportViewersByCoeId(selectedRow.id);
            } else {
                response = await getAllAdmins(selectedRow.id);
            }
            let dataRows = response?.data?.data || [];
            if (adminListType === 'admin') {
                dataRows = dataRows.filter(admin => admin.role?.toLowerCase() !== 'coe_report_viewer');
            }
            setRows(dataRows);
        } catch (error) {
            setRows([]);
            toast.error(error?.response?.data?.message || "Something went wrong while fetching data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setPage(0);
            setRows([]);
            fetchData();
        } else {
            setRows([]);
            setLoading(false);
        }
    }, [selectedRow, open]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredLearners = rows.filter((admin) =>
        admin.display_name.toLowerCase().includes(search.toLowerCase()) ||
        admin.email.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedRows = filteredLearners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    function highlightText(text, search) {
        if (!search) return text;
        const parts = text.split(new RegExp(`(${search})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === search.toLowerCase() ? <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
        );
    }

    const handleCopyLink = async () => {
        const coePath = selectedRow?.name ? selectedRow.name.trim().replace(/\s+/g, '-') : 'college-viewer';
        let baseUrl = window.location.origin;
        if (window.location.hostname === "admin.ejet.ai" || baseUrl.includes("admin.ejet.ai")) {
            baseUrl = "https://reports.ejet.ai";
        }
        const link = `${baseUrl}/${coePath}`;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = link;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            setCopied(true);
            toast.success("Viewer link copied to clipboard!");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error("Failed to copy link to clipboard");
        }
    };

    const handleConfirmDisable = (admin) => {
        setSelectedAdmin(admin);
        setConfirmOpen(true);
    };

    const handleConfirmYes = async () => {
        try {
            const response = await unmapAdmin(selectedRow.id, selectedAdmin.ejet_admin_id);
            toast.success(response.data.message);
            fetchCoeData();
            fetchData();
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        } finally {
            setConfirmOpen(false);
            setSelectedAdmin(null);
        }
    };

    const handleConfirmNo = () => {
        setConfirmOpen(false);
        setSelectedAdmin(null);
    };

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={modalStyle}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: "nowrap", mb: 0 }}>
                                {adminListType === 'viewer' ? 'Viewer Details' : 'Admin Details'}
                            </Typography>
                            <TextField
                                sx={{ width: "350px" }}
                                size="small"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            {adminListType === 'viewer' && (
                                <Tooltip title={copied ? "Link Copied!" : selectedRow?.name ? `Copy ${selectedRow.name} Link` : "Copy Viewer Link"} arrow>
                                    <IconButton
                                        size="small"
                                        color={copied ? "success" : "primary"}
                                        onClick={handleCopyLink}
                                        aria-label="copy viewer link"
                                        sx={{
                                            border: "1px solid",
                                            borderColor: copied ? "success.main" : "#c4c4c4",
                                            borderRadius: "8px",
                                            p: "7px",
                                            backgroundColor: copied ? "rgba(46, 125, 50, 0.08)" : "transparent",
                                            "&:hover": {
                                                borderColor: "primary.main",
                                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            }
                                        }}
                                    >
                                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {user?.role === 'super_admin' && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ textTransform: "capitalize", whiteSpace: "nowrap", borderRadius: "8px" }}
                                        onClick={() => setAddAdminOpen(true)}
                                    >
                                        {adminListType === 'viewer' ? 'Assign Viewer' : 'Add Admin'}
                                    </Button>
                                    {adminListType === 'viewer' && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{ textTransform: "capitalize", whiteSpace: "nowrap", borderRadius: "8px" }}
                                            onClick={() => setAddNewViewerOpen(true)}
                                        >
                                            Add New Viewer
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>

                    <TableContainer sx={{ maxHeight: "450px !important" }} component={Paper}>
                        <Table sx={{ tableLayout: "auto", width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Name</StyledTableCell>
                                    <StyledTableCell>Email</StyledTableCell>
                                    <StyledTableCell>Role</StyledTableCell>
                                    <StyledTableCell>Mobile</StyledTableCell>
                                    {user?.role === 'super_admin' && (
                                        <StyledTableCell>Actions</StyledTableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                                                <CircularProgress size={36} />
                                                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                                                    Loading {adminListType === 'viewer' ? 'viewer' : 'admin'} details...
                                                </Typography>
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : paginatedRows.length > 0 ? (
                                    paginatedRows.map((admin, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>{highlightText(admin?.display_name, search) || "-"}</StyledTableCell>
                                            <StyledTableCell style={{ textTransform: "none" }}>{highlightText(admin?.email, search) || "-"}</StyledTableCell>
                                            <StyledTableCell>{admin.role || "-"}</StyledTableCell>
                                            <StyledTableCell>{admin.mobile_number || "-"}</StyledTableCell>
                                            {user?.role === 'super_admin' && (
                                                <StyledTableCell>
                                                    <Tooltip title="Disable" arrow>
                                                        <IconButton size="small" aria-label="disable" onClick={() => handleConfirmDisable(admin)}>
                                                            <BlockIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </StyledTableCell>
                                            )}
                                        </StyledTableRow>
                                    ))
                                ) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            {search ? `No ${adminListType === 'viewer' ? 'viewer' : 'admin'} found matching your search` : `No ${adminListType === 'viewer' ? 'viewer' : 'admin'} data available`}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 30, 60, 100]}
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            </Modal>

            <AddAdminToCoe
                open={addAdminOpen}
                onClose={() => setAddAdminOpen(false)}
                coeId={selectedRow?.id}
                fetchData={fetchData}
                fetchCoeData={fetchCoeData}
                adminListType={adminListType}
                onOpenAddNewViewer={() => setAddNewViewerOpen(true)}
            />

            {adminListType === 'viewer' && (
                <AddNewViewerModal
                    open={addNewViewerOpen}
                    onClose={() => setAddNewViewerOpen(false)}
                    coeId={selectedRow?.id}
                    coeName={selectedRow?.name}
                    fetchData={fetchData}
                    fetchCoeData={fetchCoeData}
                    onSwitchToAssignExisting={() => setAddAdminOpen(true)}
                />
            )}

            <Dialog
                open={confirmOpen}
                onClose={handleConfirmNo}
                PaperProps={{
                    sx: {
                        p: 3,
                        borderRadius: 3,
                        boxShadow: 6,
                        minWidth: 400,
                        maxWidth: 450,
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        color: '#d32f2f',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <BlockIcon fontSize="medium" color="error" />
                    Confirm Unmapping
                </DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ fontSize: 15, color: "#555", mt: 1 }}>
                        Are you sure you want to remove <strong>{selectedAdmin?.display_name}</strong> from this COE?
                        <br />
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "flex-end", mt: 2 }}>
                    <Button
                        onClick={handleConfirmNo}
                        variant="outlined"
                        color="inherit"
                        sx={{ textTransform: "capitalize", borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmYes}
                        variant="contained"
                        color="error"
                        sx={{ textTransform: "capitalize", borderRadius: 2 }}
                    >
                        Yes, Remove
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default AdminListModal;
